import { readApiJson } from '../utils/readApiJson';

const API_BASE_URL = '/api/prisme';

export type ClientOnboardingStatus = 'À faire' | 'En cours' | 'Bloqué' | 'Fait' | 'N/A';
export type ClientOnboardingPriority = 'Critique' | 'Haute' | 'Moyenne' | 'Basse' | '';

export interface ClientOnboardingItem {
  id?: number;
  onboarding_id?: number;
  section_key: string;
  section_title: string;
  item_order: number;
  checked: boolean;
  priorite?: ClientOnboardingPriority;
  statut?: ClientOnboardingStatus | '';
  element: string;
  notes?: string;
  action?: string;
  responsable?: string;
  echeance?: string | null;
}

export interface ClientOnboarding {
  id?: number;
  client_id: number | '';
  technicien_id?: number | '';
  titre: string;
  statut: ClientOnboardingStatus;
  priorite: Exclude<ClientOnboardingPriority, ''>;
  urgence_ti?: string;
  site_principal?: string;
  contact_principal?: string;
  periode_couverte?: string;
  prepare_pour?: string;
  avancement_global?: string;
  risques_critiques?: string;
  actions_ouvertes?: string;
  decisions_requises?: string;
  prochaine_rencontre?: string;
  compte_rendu?: {
    resume_executif?: string;
    points_valides?: string;
    points_a_corriger?: string;
    recommandations_prioritaires?: string;
    prochaines_etapes?: string;
  };
  client_nom?: string;
  technicien_nom?: string;
  technicien_email?: string;
  total_items?: number;
  completed_items?: number;
  high_priority_open_items?: number;
  created_at?: string;
  updated_at?: string;
  archived?: boolean;
  archived_at?: string | null;
  items?: ClientOnboardingItem[];
  history?: ClientOnboardingHistory[];
}

export interface ClientOnboardingHistory {
  id: number;
  onboarding_id: number;
  event_type: string;
  actor_email?: string;
  created_at: string;
  snapshot?: Record<string, unknown>;
}

export const onboardingStatuses: ClientOnboardingStatus[] = ['À faire', 'En cours', 'Bloqué', 'Fait', 'N/A'];
export const onboardingPriorities: Exclude<ClientOnboardingPriority, ''>[] = ['Critique', 'Haute', 'Moyenne', 'Basse'];

export const CLIENT_ONBOARDING_TEMPLATE: Array<{ key: string; title: string; items: string[] }> = [
  {
    key: 'informations-generales',
    title: 'Informations générales du client',
    items: [
      'Nom légal de l’entreprise',
      'Nom commercial',
      'Adresse principale',
      'Personnes contacts',
      'Contact décisionnel',
      'Contact urgence TI',
      'Heures d’ouverture',
      'Sites physiques / succursales',
      'Nombre d’utilisateurs',
      'Nombre de postes',
      'Nombre de serveurs',
      'Domaine principal',
      'Fournisseurs TI actuels',
    ],
  },
  {
    key: 'acces-comptes-admin',
    title: 'Accès et comptes administratifs',
    items: [
      'Obtenir les accès administratifs existants',
      'Identifier les comptes privilégiés',
      'Changer / sécuriser les mots de passe critiques',
      'Activer MFA sur tous les comptes administrateurs',
      'Créer un compte admin dédié pour l’équipe TI',
      'Documenter les accès dans le gestionnaire de mots de passe',
      'Vérifier les accès Microsoft 365',
      'Vérifier les accès registrar DNS',
      'Vérifier les accès hébergement web',
      'Vérifier les accès pare-feu / réseau',
      'Vérifier les accès serveurs / NAS / sauvegardes',
      'Vérifier les accès antivirus / EDR',
      'Vérifier les accès téléphonie / fournisseur Internet',
    ],
  },
  {
    key: 'inventaire-actifs-ti',
    title: 'Inventaire des actifs TI',
    items: [
      'Postes de travail',
      'Portables',
      'Serveurs physiques',
      'Serveurs virtuels',
      'NAS / stockage',
      'Imprimantes',
      'Switchs',
      'Pare-feu / routeurs',
      'Points d’accès Wi-Fi',
      'Téléphones IP',
      'Licences logicielles',
      'Équipements hors garantie',
      'Équipements critiques',
      'Numéros de série',
      'Dates d’achat si disponibles',
      'Garantie / support actif',
    ],
  },
  {
    key: 'microsoft-365',
    title: 'Microsoft 365 / Tenant',
    items: [
      'Prendre contrôle du tenant Microsoft 365',
      'Vérifier les rôles administrateurs',
      'Vérifier les domaines validés',
      'Vérifier MFA / Conditional Access',
      'Vérifier les licences',
      'Vérifier comptes inactifs / invités',
      'Vérifier groupes / boîtes partagées',
      'Vérifier Exchange / SPF / DKIM / DMARC',
      'Vérifier SharePoint / OneDrive',
      'Vérifier Teams',
      'Vérifier politiques de sécurité',
      'Vérifier sauvegarde M365 si applicable',
    ],
  },
  {
    key: 'securite',
    title: 'Sécurité',
    items: [
      'Vérifier antivirus / EDR',
      'Vérifier MFA',
      'Vérifier comptes administrateurs',
      'Vérifier pare-feu',
      'Vérifier mises à jour critiques',
      'Vérifier politiques de mots de passe',
      'Vérifier sauvegardes protégées',
      'Vérifier alertes de sécurité',
      'Vérifier accès distant / VPN / RDP',
      'Identifier risques critiques immédiats',
    ],
  },
  {
    key: 'sauvegardes-continuite',
    title: 'Sauvegardes / continuité',
    items: [
      'Identifier solution de sauvegarde',
      'Vérifier derniers backups',
      'Vérifier erreurs récentes',
      'Vérifier couverture serveurs / postes / M365',
      'Vérifier rétention',
      'Vérifier sauvegardes hors site / immuables',
      'Tester restauration',
      'Documenter RPO / RTO',
      'Documenter procédure de restauration',
      'Identifier données critiques non sauvegardées',
    ],
  },
  {
    key: 'reseau-internet-wifi',
    title: 'Réseau / Internet / Wi-Fi',
    items: [
      'Documenter topologie réseau',
      'Vérifier pare-feu / routeur principal',
      'Vérifier switches',
      'Vérifier VLAN',
      'Vérifier Wi-Fi',
      'Vérifier firmware des équipements critiques',
      'Documenter accès fournisseur Internet',
      'Documenter IP publique',
      'Vérifier règles NAT / port forwarding',
      'Vérifier VPN site-à-site ou client VPN',
    ],
  },
  {
    key: 'postes-gestion',
    title: 'Postes et gestion',
    items: [
      'Vérifier domaine AD ou Azure AD / Entra ID',
      'Vérifier Intune / GPO / RMM',
      'Installer agent RMM si applicable',
      'Installer agent antivirus / EDR',
      'Vérifier droits admin locaux',
      'Vérifier standardisation des postes',
      'Vérifier logiciels métier installés',
      'Vérifier imprimantes déployées',
      'Vérifier mises à jour OS',
      'Vérifier chiffrement disque',
    ],
  },
  {
    key: 'applications-metier',
    title: 'Applications métier',
    items: [
      'Liste des applications critiques',
      'Fournisseurs / contacts support',
      'Type d’installation : local / cloud / serveur / SaaS',
      'Comptes administrateurs',
      'Méthode de sauvegarde',
      'Procédure de restauration',
      'Dépendances réseau / serveur',
      'Licences',
      'Contrats de support',
    ],
  },
  {
    key: 'documentation',
    title: 'Documentation',
    items: [
      'Créer fiche client',
      'Créer schéma réseau',
      'Documenter les accès',
      'Documenter procédures importantes',
      'Documenter licences',
      'Documenter fournisseurs',
      'Documenter contrats',
      'Documenter garanties',
      'Documenter architecture M365',
      'Documenter sauvegardes',
      'Documenter risques connus',
      'Documenter actions urgentes recommandées',
    ],
  },
  {
    key: 'priorites-apres-audit',
    title: 'Priorités après audit',
    items: [
      'Risques critiques à corriger immédiatement',
      'Correctifs sécurité rapides',
      'Nettoyage des accès',
      'Normalisation des licences',
      'Mise en place sauvegardes fiables',
      'Plan de migration / amélioration',
      'Recommandations au client',
      'Rapport initial de prise en charge',
    ],
  },
];

export function createTemplateItems(): ClientOnboardingItem[] {
  return CLIENT_ONBOARDING_TEMPLATE.flatMap((section, sectionIndex) =>
    section.items.map((element, itemIndex) => ({
      section_key: section.key,
      section_title: section.title,
      item_order: sectionIndex * 100 + itemIndex,
      checked: false,
      priorite: '',
      statut: 'À faire',
      element,
      notes: '',
      action: '',
      responsable: '',
      echeance: null,
    })),
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  const data = await readApiJson<T | { error?: string }>(res);
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Erreur API prise en charge');
  return data as T;
}

export async function listClientOnboardings(filters: Record<string, string | number | boolean | undefined> = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') params.set(key, String(value));
  });
  return request<ClientOnboarding[]>(`/client-onboardings${params.toString() ? `?${params}` : ''}`);
}

export async function getClientOnboarding(id: number) {
  return request<ClientOnboarding>(`/client-onboardings/${id}`);
}

export async function createClientOnboarding(onboarding: ClientOnboarding, items: ClientOnboardingItem[]) {
  return request<ClientOnboarding>('/client-onboardings', {
    method: 'POST',
    body: JSON.stringify({ onboarding, items }),
  });
}

export async function updateClientOnboarding(id: number, onboarding: ClientOnboarding, items: ClientOnboardingItem[]) {
  return request<ClientOnboarding>(`/client-onboardings/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ onboarding, items }),
  });
}

export async function deleteClientOnboarding(id: number) {
  return request<{ success: boolean }>(`/client-onboardings/${id}`, { method: 'DELETE' });
}

export async function restoreClientOnboarding(id: number) {
  return request<ClientOnboarding>(`/client-onboardings/${id}/restore`, { method: 'POST' });
}
